/**
 * SEO Content Registry
 * ===================
 * Central source of truth for all approved, factual content used in SEO page generation.
 * 
 * RULES:
 * 1. NEVER invent or fabricate content - only use pre-approved text
 * 2. All pricing must be fetched from APIs and hard-stored
 * 3. All claims must have sources (Strømligning, ELBIIL, etc.)
 * 4. Use synonyms/variations to avoid duplicate content
 */

// ============================================
// CORE PRODUCT DATA
// ============================================
export const ENKEL_PRODUCTS = {
  subscriptions: {
    kvartal: {
      name: 'enkel kvartal',
      monthlyPrice: 18,
      yearlyPrice: 216,
      spotTillaeg: 0,
      binding: false,
      description: 'Kvartalsafregning med spotpris uden tillæg',
      features: ['Spotpris uden tillæg', 'Kvartalsafregning', 'Ingen binding', 'Betalingsservice inkluderet']
    },
    stroem: {
      name: 'enkel strøm',
      monthlyPrice: 49,
      yearlyPrice: 588,
      spotTillaeg: 0,
      binding: false,
      description: 'Månedsafregning med spotpris uden tillæg',
      features: ['Spotpris uden tillæg', 'Månedsafregning', 'Ingen binding', 'Betalingsservice inkluderet']
    },
    natur: {
      name: 'enkel natur',
      monthlyPrice: 69,
      yearlyPrice: 828,
      spotTillaeg: 0,
      binding: false,
      description: 'Grøn strøm med månedsafregning',
      features: ['100% grøn strøm', 'Spotpris uden tillæg', 'Månedsafregning', 'Ingen binding']
    }
  },
  chargers: {
    nexblue_edge_2: {
      id: 'nexblue_edge_2',
      name: 'NexBlue Edge 2',
      brand: 'NexBlue',
      price: 3499,
      originalPrice: 3999,
      kw: 22,
      slug: 'nexblue-edge-2',
      features: ['22 kW ladekraft', 'Wi-Fi forbindelse', 'V2X Ready', 'LED status-ring', 'IP65 vejrbestandig'],
      image: 'https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/2420af247_1770bf7e0_Black-Right-Front_BLUE-LIGHT-ON_1_1_optimized_200.png',
      compatibility: ['enkel Saving Mode', 'Alle elbiler med Type 2']
    },
    zaptec_go: {
      id: 'zaptec_go',
      name: 'Zaptec Go',
      brand: 'Zaptec',
      price: 4499,
      originalPrice: 4499,
      kw: 22,
      slug: 'zaptec-go',
      features: ['22 kW ladekraft', 'Wi-Fi + 4G', 'Load balancing', 'IP65 vejrbestandig', 'Kompakt design'],
      image: 'https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/fbda7ccfd_708b28a83_zaptec_go_midnight-blue_TURNTABLE_v009_Midnight_Blue_1500px.png',
      compatibility: ['enkel Saving Mode', 'Alle elbiler med Type 2']
    },
    zaptec_go_2: {
      id: 'zaptec_go_2',
      name: 'Zaptec Go 2',
      brand: 'Zaptec',
      price: 5999,
      originalPrice: 5999,
      kw: 22,
      slug: 'zaptec-go-2',
      features: ['22 kW ladekraft', 'Wi-Fi + 4G', 'V2G Ready', 'ISO 15118', 'MID-certificeret'],
      image: 'https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/856c78c5f_460793e4d_Zaptec_Go_2_Productimage_quater_asphalt_black.png',
      compatibility: ['enkel Saving Mode', 'Alle elbiler med Type 2', 'V2G fremtidssikret']
    }
  },
  installation: {
    existing: { price: 1300, description: 'Eksisterende stik' },
    standard: { price: 3000, description: 'Standard installation' }
  }
};

// ============================================
// SERVICE AGREEMENT CONTENT
// ============================================
export const SERVICE_AGREEMENT = {
  price: 0,
  marketPrice: 69,
  yearlyValue: 828,
  includes: [
    { label: 'Saving Mode', description: 'Automatisk opladning i de billigste timer', value: '0 kr./md.' },
    { label: 'Laderabat', description: '10 øre rabat pr. kWh ved opladning via appen', value: '10 øre/kWh' },
    { label: 'enkel app', description: 'Fuld kontrol, statistik og forbrugsoverblik', value: '0 kr./md.' },
    { label: 'Elafgift-refusion', description: 'Automatisk ansøgning – du gør ingenting', value: 'Inkluderet' },
    { label: 'Support', description: 'Fejlfinding og support via app og kundeservice', value: 'Inkluderet' },
    { label: 'Betalingsservice', description: 'Automatisk betaling', value: '0 kr./md.' }
  ],
  compatibleChargers: ['Zaptec Go', 'Zaptec Go 2', 'Zaptec Pro', 'NexBlue Edge 2', 'NexBlue Pro', 'Easee Home', 'Easee Charge'],
  requirements: [
    'Aktiv el-aftale hos enkel energi',
    'Kompatibel ladeboks (Zaptec, NexBlue eller Easee)',
    'Ladeboks tilsluttet enkel platform'
  ]
};

// ============================================
// LADERABAT (CHARGING REBATE) CONTENT
// ============================================
export const LADERABAT = {
  rebateOrePerKwh: 10,
  rebateKrPerKwh: 0.10,
  avgDanishEvKwhPerYear: 2800,
  requirements: [
    'Aktiv el-aftale hos enkel energi gennem hele måneden',
    'Kompatibel ladeboks tilsluttet enkel platform',
    'Al hjemmeopladning via enkel Saving Mode app',
    'Ladeboksen online og tilsluttet hele måneden'
  ],
  howItWorks: [
    { step: 1, title: 'Lad via enkel app', description: 'Brug enkel Saving Mode app til at starte din opladning. Rabatten aktiveres automatisk.' },
    { step: 2, title: 'Rabat fratrækkes', description: 'Du betaler 10 øre (inkl. moms) mindre end spotprisen pr. kWh, der lades hjemme via Saving Mode i enkel-appen (kompatibel ladeboks og bil). Manuel opladning giver ikke rabat.' },
    { step: 3, title: 'Spar automatisk', description: 'Hver kWh du lader via enkel app giver dig 10 øre tilbage – uden ekstra handling fra din side.' }
  ],
  disclaimers: {
    notIntroOffer: 'Laderabatten er ikke et introduktionstilbud – det er en løbende fordel baseret på vores indtjening på elmarkedet.',
    canChange: 'Rabatten på 0,10 kr./kWh er fastsat for en overskuelig fremtid og kan justeres baseret på markedsforholdene. Ændringer varsles forudgående.',
    onlyEnkelApp: 'Gælder kun ved opladning via enkel Saving Mode app – ikke tredjepartsapps.'
  }
};

// ============================================
// SAVING MODE CONTENT
// ============================================
export const SMART_CHARGE = {
  estimatedSavingsPct: 30,
  historicalRange: '15-30%',
  appCost: 0,
  marketAppCost: 99,
  features: [
    { label: 'Prisoptimering', description: 'Analyserer time-for-time priser og planlægger opladning i de billigste timer automatisk.' },
    { label: 'Afgangstids-planlægning', description: 'Sæt hvornår du skal bruge bilen – vi sørger for den er klar med optimal ladning.' },
    { label: 'Fuld kontrol via app', description: 'Start/stop opladning, se statistik og juster indstillinger – alt fra din telefon.' }
  ],
  disclaimer: '*Op til ca. 30% estimeret besparelse afhænger af spotprisudsving og opladningsmønster. Historiske data viser 15-30%, men fremtidige resultater kan variere og er ikke garanteret.'
};

// ============================================
// EV CALCULATION CONSTANTS
// ============================================
export const EV_CONSTANTS = {
  avgKwhPer100km: 22, // 0.22 kWh/km
  avgDanishKmPerYear: 40000,
  defaultHomeChargePct: 0.90,
  
  calculateEvKwh: (kmPerYear, homeChargePct = 0.90) => {
    return Math.round(kmPerYear * 0.22 * homeChargePct);
  },
  
  calculateLaderabat: (kwhPerYear) => {
    return Math.round(kwhPerYear * 0.10);
  },
  
  calculateSmartChargeSavings: (kwhPerYear, avgSpotPrice = 0.72) => {
    return Math.round(kwhPerYear * avgSpotPrice * 0.22);
  }
};

// ============================================
// RECOGNITION & TRUST BADGES
// ============================================
export const TRUST_BADGES = {
  stromligning: {
    name: 'Strømligning.dk',
    score: '5/5',
    logo: 'https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/da42c3e86_953b544b6_image.png',
    link: 'https://stromligning.dk/?forbrugsprofil=10000',
    description: 'Certificeret af Strømligning.dk med topkarakter',
    claims: ['5 ud af 5 stjerner', 'Vurderet på pris, gennemsigtighed og service', 'Uafhængig vurdering']
  },
  elbiil: {
    name: 'ELBIIL.dk',
    score: '5/5',
    logo: 'https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/f14b91d3d_ede956df9_image.png',
    link: 'https://www.elbiil.dk/',
    description: 'Kåret som billigste elbil-ladeløsning',
    claims: ['14 elbil-ladeløsninger testet', 'Billigste samlede pris', 'Inkluderer hardware og el']
  },
  nicolaskj: {
    name: 'NicolasKJ.dk',
    date: 'Januar 2026',
    description: 'Lavest på pris i NicolasKJ.dk’s uafhængige test (jan. 2026)',
    claims: ['50+ elselskaber analyseret', 'Baseret på reelle priser', 'Ingen affiliatelinks'],
    link: '/blogbestitestnicolaskj'
  },
  taenk: {
    name: 'Forbrugerrådet Tænk',
    date: '2026',
    description: 'Tre produkter i top 10 i Tænks eltest',
    claims: ['Top 10 placering', 'For elbilister endnu billigere end tabellen viser'],
    link: '/blogartikel-taenk-test'
  }
};

// ============================================
// DANISH GEOGRAPHIC DATA
// ============================================
export const DANISH_REGIONS = [
  { id: 'hovedstaden', name: 'Hovedstaden', zone: 'DK2', population: 1850000 },
  { id: 'sjaelland', name: 'Sjælland', zone: 'DK2', population: 835000 },
  { id: 'syddanmark', name: 'Syddanmark', zone: 'DK1', population: 1220000 },
  { id: 'midtjylland', name: 'Midtjylland', zone: 'DK1', population: 1330000 },
  { id: 'nordjylland', name: 'Nordjylland', zone: 'DK1', population: 590000 }
];

export const PRICE_ZONES = {
  DK1: { name: 'Vestdanmark', description: 'Jylland og Fyn' },
  DK2: { name: 'Østdanmark', description: 'Sjælland, Lolland-Falster og Bornholm' }
};

// ============================================
// CONTENT BLOCK SYNONYMS (for variation)
// ============================================
export const SYNONYMS = {
  save: ['spar', 'reducer', 'bespare', 'undgå at betale', 'få rabat på'],
  cheap: ['billig', 'lav pris', 'prisvenlig', 'økonomisk', 'konkurrencedygtig'],
  included: ['inkluderet', 'uden ekstra betaling', 'gratis', 'til 0 kr.', 'uden ekstra omkostninger'],
  automatic: ['automatisk', 'uden handling fra din side', 'helt af sig selv', 'uden du skal gøre noget'],
  switch: ['skift', 'flyt', 'kom over til', 'vælg', 'gå til']
};

// ============================================
// FAQ TEMPLATES
// ============================================
export const FAQ_TEMPLATES = {
  competitor: (competitorName, savings) => [
    {
      question: `Er enkel energi billigere end ${competitorName}?`,
      answer: savings > 0 
        ? `Ja. Ved 9.000 kWh årligt forbrug sparer du ca. ${savings.toLocaleString('da-DK')} kr./år ved at skifte fra ${competitorName} til enkel energi. Besparelsen skyldes primært, at enkel ikke lægger tillæg oveni spotprisen. Med elbil er besparelsen endnu større pga. laderabat (10 øre/kWh) og Saving Mode (op til ~30% besparelse).`
        : `Priserne er sammenlignelige. Men hos enkel får du også Saving Mode, serviceaftale og laderabat inkluderet – en ekstraværdi på over 2.000 kr./år.`
    },
    {
      question: `Hvad får jeg hos enkel, som ${competitorName} ikke tilbyder?`,
      answer: `enkel energi inkluderer Saving Mode (automatisk billig opladning, værdi op til 99 kr./md.), serviceaftale til ladeboks (værdi 69 kr./md.), laderabat på 10 øre/kWh og automatisk elafgift-refusion – alt til 0 kr./md. ekstra. ${competitorName} tilbyder ikke disse tjenester. Derudover har vi ingen binding og 0 øre tillæg til spotprisen.`
    },
    {
      question: `Skal jeg selv opsige hos ${competitorName}?`,
      answer: `Nej. Når du tilmelder dig enkel energi, håndterer vi opsigelsen hos ${competitorName} automatisk. Du behøver ikke kontakte ${competitorName} selv – vi klarer alt.`
    },
    {
      question: 'Hvad koster det at skifte elselskab?',
      answer: 'Det koster 0 kr. at skifte til enkel energi. Ingen oprettelsesgebyr, ingen binding, ingen opsigelsesgebyr. Tilmelding tager 5 minutter online.'
    },
    {
      question: 'Hvad er Saving Mode?',
      answer: 'Saving Mode finder automatisk de billigste timer og planlægger din elbil-opladning. Historiske data viser besparelser på 15-30%. Kræver kompatibel ladeboks (Zaptec, NexBlue eller Easee) og serviceaftale (inkluderet til 0 kr./md.).'
    },
    {
      question: 'Hvad er laderabatten?',
      answer: 'Laderabatten giver dig 10 øre rabat af spotprisen pr. kWh når du oplader via enkel Saving Mode app. Rabatten fratrækkes automatisk din el-regning. Ved 40.000 km/år og 90% hjemmeopladning dækker laderabatten hele dit abonnement – og det resterende beløb modregnes din el-regning.'
    },
    {
      question: `Hvem er ${competitorName}?`,
      answer: `${competitorName} er et dansk elselskab. Som mange konkurrenter opkræver ${competitorName} enten et tillæg til spotprisen, et højere abonnement, eller begge dele. Hos enkel energi betaler du kun spotpris + 0 øre tillæg fra 18 kr./md.`
    }
  ],
  
  city: (cityName, zone) => [
    {
      question: `Hvem er det billigste elselskab i ${cityName}?`,
      answer: `Hos Strømligning.dk er enkel energi konsekvent blandt de billigste elselskaber i ${cityName} (${zone}). Vi tilbyder spotpris uden tillæg + abonnement fra kun 18 kr./md.`
    },
    {
      question: `Kan jeg få enkel energi i ${cityName}?`,
      answer: `Ja. Alle danske husstande kan blive kunde hos enkel energi – uanset hvor i Danmark du bor. Tilmelding tager 5 minutter.`
    },
    {
      question: `Hvad er prisforskellen mellem DK1 og DK2 i ${cityName}?`,
      answer: `${cityName} ligger i ${zone} (${zone === 'DK1' ? 'Vestdanmark' : 'Østdanmark'}). Spotprisen varierer mellem zonerne, men enkel energi tilbyder altid spotpris uden tillæg uanset zone.`
    },
    {
      question: 'Har I ladebokse til elbil?',
      answer: 'Ja. Vi tilbyder ladebokse fra Zaptec og NexBlue med Saving Mode og serviceaftale inkluderet. Installation fra 1.300 kr.'
    }
  ],
  
  charger: (chargerName) => [
    {
      question: `Hvad koster ${chargerName}?`,
      answer: `Se aktuel pris på vores hjemmeside. Prisen inkluderer el-aftale, Saving Mode og serviceaftale. Du betaler ikke noget i dag – opkræves typisk via din første el-regning eller ved afsendelse.`
    },
    {
      question: `Er Saving Mode inkluderet med ${chargerName}?`,
      answer: 'Ja. Saving Mode er inkluderet til 0 kr./md. når du har el-aftale hos enkel energi. Det samme gælder serviceaftale og laderabat.'
    },
    {
      question: `Hvad er installationsprisen for ${chargerName}?`,
      answer: 'Standard installation koster 3.000 kr. Har du eksisterende stik, koster det 1.300 kr. Installation udføres af autoriseret elinstallatør.'
    },
    {
      question: 'Kan jeg bruge min egen ladeboks?',
      answer: 'Ja, hvis din ladeboks er kompatibel (Zaptec, NexBlue eller Easee) kan du tilslutte den til enkel Saving Mode og få alle fordelene.'
    }
  ]
};

// ============================================
// INTERNAL LINKING STRUCTURE
// ============================================
export const INTERNAL_LINKS = {
  elaftale: { path: '/elaftale-b', anchor: 'Se el-aftaler', priority: 'high' },
  opladere: { path: '/opladere', anchor: 'Se ladebokse', priority: 'high' },
  elbil: { path: '/elbil', anchor: 'Elbil-løsning', priority: 'high' },
  smartCharge: { path: '/portal/smart-charge', anchor: 'Saving Mode', priority: 'medium' },
  laderabat: { path: '/el-fordele#laderabat', anchor: 'Laderabat', priority: 'medium' },
  serviceAftale: { path: '/service-aftale', anchor: 'Serviceaftale', priority: 'medium' },
  sammenlign: { path: '/elpris-sammenligning', anchor: 'Sammenlign elselskaber', priority: 'medium' },
  faq: { path: '/supportkundeportal', anchor: 'FAQ', priority: 'low' },
  
  chargers: {
    nexblue: { path: '/nexblue-edge-2', anchor: 'NexBlue Edge 2', priority: 'medium' },
    zaptecGo: { path: '/zaptec-go', anchor: 'Zaptec Go', priority: 'medium' },
    zaptecGo2: { path: '/zaptec-go-2', anchor: 'Zaptec Go 2', priority: 'medium' }
  },
  
  blog: {
    nicolaskj: { path: '/blogbestitestnicolaskj', anchor: 'NicolasKJ test', priority: 'low' },
    elbiilTest: { path: '/blogartikelelbiiltest', anchor: 'ELBIIL.dk test', priority: 'low' },
    taenkTest: { path: '/blogartikel-taenk-test', anchor: 'Tænk test', priority: 'low' }
  }
};

// ============================================
// SCHEMA.ORG TEMPLATES
// ============================================
export const SCHEMA_TEMPLATES = {
  FAQPage: (faqs) => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  }),
  
  Product: (product) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "brand": { "@type": "Brand", "name": product.brand },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "DKK",
      "price": product.price,
      "availability": "https://schema.org/InStock"
    }
  }),
  
  LocalBusiness: {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "enkel energi",
    "url": "https://enkelenergi.dk",
    "telephone": "+45 70 60 80 07",
    "email": "hej@enkelenergi.dk",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bryghuspladsen 8",
      "addressLocality": "København K",
      "postalCode": "1473",
      "addressCountry": "DK"
    }
  }
};

export default {
  ENKEL_PRODUCTS,
  SERVICE_AGREEMENT,
  LADERABAT,
  SMART_CHARGE,
  EV_CONSTANTS,
  TRUST_BADGES,
  DANISH_REGIONS,
  PRICE_ZONES,
  SYNONYMS,
  FAQ_TEMPLATES,
  INTERNAL_LINKS,
  SCHEMA_TEMPLATES
};